import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Users, Search, ShieldAlert, Trash2, Settings2, Building2, FolderLock, History, Compass, ArrowUpRight, Globe2, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import client from "@/api/client";
import { BulkToolbar, useBulkSelection } from "@/components/admin/BulkToolbar";
import { AdminUserAccessDialog } from "@/components/admin/AdminUserAccessDialog";
import { AdminUserActivityDialog } from "@/components/admin/AdminUserActivityDialog";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

function SortIcon({ active, dir }) {
  if (!active) return <ChevronsUpDown className="h-3 w-3 opacity-40" />;
  return dir === "asc" ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" />;
}

export function AdminUsersManager() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const roleLabel = (r) => t(`roles.${r}`, { defaultValue: String(r || "").replace(/^./, (c) => c.toUpperCase()) });
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState(["admin", "founder", "investor", "advisor", "viewer"]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  // Seed role filter from `?role=founder` so the Overview → Users-by-role
  // drill-through lands on the filtered view immediately.
  const [roleFilter, setRoleFilter] = useState(searchParams.get("role") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [btFilter, setBtFilter] = useState(searchParams.get("business_type") || "");
  const [countryFilter, setCountryFilter] = useState(searchParams.get("country") || "");
  const [countries, setCountries] = useState([]);
  const [sort, setSort] = useState("created");
  const [sortDir, setSortDir] = useState("desc");
  const [businessTypes, setBusinessTypes] = useState([]);
  const [noBtCount, setNoBtCount] = useState(0);
  const [saving, setSaving] = useState(null);
  const [bulkRole, setBulkRole] = useState("viewer");
  const [manageUser, setManageUser] = useState(null);
  const [activityUser, setActivityUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (roleFilter) params.set("role", roleFilter);
      if (statusFilter) params.set("status", statusFilter);
      if (btFilter) params.set("business_type", btFilter);
      if (countryFilter) params.set("country", countryFilter);
      params.set("sort", sort);
      params.set("dir", sortDir);
      const res = await client.get(`/admin/users${params.toString() ? `?${params}` : ""}`);
      setItems(res.data?.items || []);
      if (Array.isArray(res.data?.countries)) setCountries(res.data.countries);
      if (Array.isArray(res.data?.allowed_roles)) setRoles(res.data.allowed_roles);
      if (Array.isArray(res.data?.business_types)) setBusinessTypes(res.data.business_types);
      setNoBtCount(res.data?.without_business_type || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("usersManager.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [q, roleFilter, statusFilter, btFilter, countryFilter, sort, sortDir, t]);

  const toggleSort = (key) => {
    if (sort === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setSortDir(key === "created" ? "desc" : "asc");
    }
  };

  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [load]);

  const bulk = useBulkSelection("users", (it) => it.user_id, load);
  const allSelected = items.length > 0 && items.every((u) => bulk.isSelected(u.user_id));

  const changeRole = async (user_id, role) => {
    setSaving(user_id);
    try {
      const res = await client.patch(`/admin/users/${user_id}`, { role });
      setItems((prev) => prev.map((u) => (u.user_id === user_id ? { ...u, ...res.data } : u)));
      toast.success(t("usersManager.roleUpdated"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("usersManager.updateFailed"));
    } finally {
      setSaving(null);
    }
  };

  const approve = async (user_id) => {
    setSaving(user_id);
    try {
      const res = await client.post(`/admin/users/${user_id}/approve`);
      setItems((prev) => prev.map((u) => (u.user_id === user_id ? { ...u, ...res.data } : u)));
      toast.success(t("usersManager.userApproved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("usersManager.approveFailed"));
    } finally {
      setSaving(null);
    }
  };

  const reject = async (user_id) => {
    setSaving(user_id);
    try {
      const res = await client.post(`/admin/users/${user_id}/reject`);
      setItems((prev) => prev.map((u) => (u.user_id === user_id ? { ...u, ...res.data } : u)));
      toast.success(t("usersManager.userRejected"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("usersManager.rejectFailed"));
    } finally {
      setSaving(null);
    }
  };

  const bulkActions = [
    {
      key: "set_role",
      label: t("usersManager.setRole", { role: roleLabel(bulkRole) }),
      icon: <ShieldAlert className="h-3.5 w-3.5" />,
      value: bulkRole,
    },
    {
      key: "delete",
      label: t("usersManager.delete"),
      icon: <Trash2 className="h-3.5 w-3.5" />,
      variant: "destructive",
      requiresConfirm: true,
      confirmTitle: t("usersManager.confirmDeleteTitle"),
      confirmBody: t("usersManager.confirmDeleteBody"),
    },
  ];

  return (
    <div className="space-y-6" data-testid="admin-panel-users">
      <Card className="rounded-2xl border border-border p-6">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Users className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-lg sm:text-xl font-semibold">{t("usersManager.title")}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t("usersManager.subtitle")}</p>
          </div>
        </div>
      </Card>

      <Card className="rounded-2xl border border-border p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_170px_200px_180px] gap-3">
          <div className="relative">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("usersManager.searchPlaceholder")}
              className="rounded-xl pl-9"
              data-testid="admin-users-search"
            />
          </div>
          <Select value={statusFilter || "_all"} onValueChange={(v) => setStatusFilter(v === "_all" ? "" : v)}>
            <SelectTrigger className="rounded-xl h-10" data-testid="admin-users-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">{t("usersManager.allStatuses")}</SelectItem>
              <SelectItem value="pending">{t("usersManager.pending")}</SelectItem>
              <SelectItem value="approved">{t("usersManager.approved")}</SelectItem>
              <SelectItem value="rejected">{t("usersManager.rejected")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={roleFilter || "_all"} onValueChange={(v) => setRoleFilter(v === "_all" ? "" : v)}>
            <SelectTrigger className="rounded-xl h-10" data-testid="admin-users-role-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">{t("usersManager.allRoles")}</SelectItem>
              {roles.map((r) => (
                <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={btFilter || "_all"} onValueChange={(v) => setBtFilter(v === "_all" ? "" : v)}>
            <SelectTrigger className="rounded-xl h-10" data-testid="admin-users-business-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all" data-testid="admin-users-bt-opt-all">{t("usersManager.allBusinessTypes", { defaultValue: "All business types" })}</SelectItem>
              {businessTypes.map((b) => (
                <SelectItem key={b.slug} value={b.slug} data-testid={`admin-users-bt-opt-${b.slug}`}>
                  {isAr && b.name_ar ? b.name_ar : b.name_en} ({b.user_count})
                </SelectItem>
              ))}
              <SelectItem value="_none" data-testid="admin-users-bt-opt-none">{t("usersManager.noBusinessType", { defaultValue: "No business type" })} ({noBtCount})</SelectItem>
            </SelectContent>
          </Select>
          <Select value={countryFilter || "_all"} onValueChange={(v) => setCountryFilter(v === "_all" ? "" : v)}>
            <SelectTrigger className="rounded-xl h-10" data-testid="admin-users-country-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="_all" data-testid="admin-users-country-opt-all">{t("usersManager.allCountries", { defaultValue: "All countries" })}</SelectItem>
              {countries.map((c) => (
                <SelectItem key={c.country} value={c.country} data-testid={`admin-users-country-opt-${c.country}`}>
                  {c.country} ({c.user_count})
                </SelectItem>
              ))}
              <SelectItem value="_none" data-testid="admin-users-country-opt-none">{t("usersManager.noCountry", { defaultValue: "No country" })}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={bulkRole} onValueChange={setBulkRole}>
            <SelectTrigger className="rounded-xl h-10" data-testid="admin-users-bulk-role" title="Role applied on bulk 'Set role'">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {roles.map((r) => (
                <SelectItem key={r} value={r}>{t("usersManager.bulkTo", { role: roleLabel(r) })}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <BulkToolbar {...bulk} actions={bulkActions} testId="admin-users-bulk" onClear={bulk.clear} />

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="admin-users-empty">
          <Users className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{t("usersManager.empty")}</p>
        </Card>
      ) : (
        <Card className="rounded-2xl border border-border p-2 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => bulk.toggleAll(items)}
                    aria-label={t("usersManager.selectAll")}
                    data-testid="admin-users-select-all"
                  />
                </th>
                <th className="px-3 py-2">
                  <button type="button" onClick={() => toggleSort("name")} className="inline-flex items-center gap-1 uppercase tracking-widest hover:text-foreground" data-testid="admin-users-sort-name">
                    {t("usersManager.colUser")}
                    <SortIcon active={sort === "name"} dir={sortDir} />
                  </button>
                </th>
                <th className="px-3 py-2">
                  <button type="button" onClick={() => toggleSort("country")} className="inline-flex items-center gap-1 uppercase tracking-widest hover:text-foreground" data-testid="admin-users-sort-country">
                    {t("profileFields.country", { defaultValue: "Country" })}
                    <SortIcon active={sort === "country"} dir={sortDir} />
                  </button>
                </th>
                <th className="px-3 py-2">{t("usersManager.colBusinessType", { defaultValue: "Business type" })}</th>
                <th className="px-3 py-2">{t("usersManager.colCompany")}</th>
                <th className="px-3 py-2">{t("usersManager.colRole")}</th>
                <th className="px-3 py-2 text-end">{t("usersManager.colActions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((u) => (
                <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={bulk.isSelected(u.user_id)}
                      onChange={() => bulk.toggle(u.user_id)}
                      aria-label={`Select ${u.name || u.user_id}`}
                      data-testid={`admin-users-select-${u.user_id}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => setActivityUser(u)}
                      className="text-start group"
                      title={t("usersManager.openUser", { defaultValue: "Open full profile" })}
                      data-testid={`admin-user-open-${u.user_id}`}
                    >
                      <span className="font-medium group-hover:text-primary group-hover:underline flex items-center gap-1.5">
                        <span className="truncate max-w-[220px]">{u.name || u.email || "—"}</span>
                        <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity rtl-flip" />
                      </span>
                      <span className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                        {u.status === "pending" ? (
                          <span className="inline-flex items-center rounded-full bg-amber-500/10 text-amber-600 border border-amber-500/30 px-1.5 text-[10px] font-medium">{t("usersManager.pending")}</span>
                        ) : u.status === "rejected" ? (
                          <span className="inline-flex items-center rounded-full bg-rose-500/10 text-rose-600 border border-rose-500/30 px-1.5 text-[10px] font-medium">{t("usersManager.rejected")}</span>
                        ) : null}
                        {u.data_room_count > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                            <FolderLock className="h-2.5 w-2.5" />{u.data_room_count}
                          </span>
                        )}
                      </span>
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs" data-testid={`admin-user-country-${u.user_id}`}>
                    {u.country || u.login_country ? (
                      <>
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/25 px-2 py-0.5 text-[11px] font-medium">
                          <Globe2 className="h-3 w-3 shrink-0" />
                          <span className="truncate max-w-[130px]">{u.country || u.login_country}</span>
                        </span>
                        {u.login_country && (
                          <span className="mt-0.5 block text-[10px] text-muted-foreground truncate max-w-[150px]" title={u.last_login_at}>
                            {t("usersManager.signedInFrom", { defaultValue: "Signed in from" })}{" "}
                            {[u.login_city, u.login_country].filter(Boolean).join(", ")}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs" data-testid={`admin-user-business-type-${u.user_id}`}>
                    {u.business_type ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/10 text-sky-700 border border-sky-500/25 px-2 py-0.5 text-[11px] font-medium" title={u.business_type.slug}>
                        <Compass className="h-3 w-3 shrink-0" />
                        <span className="truncate max-w-[140px]">
                          {isAr && u.business_type.name_ar ? u.business_type.name_ar : u.business_type.name_en}
                        </span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs" data-testid={`admin-user-company-${u.user_id}`}>
                    {u.company_name ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[11px] font-medium">
                        <Building2 className="h-3 w-3 shrink-0" />
                        <span className="truncate max-w-[130px]">{u.company_name}</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="inline-flex items-center gap-2">
                      <Select
                        value={u.role}
                        onValueChange={(v) => changeRole(u.user_id, v)}
                        disabled={saving === u.user_id}
                      >
                        <SelectTrigger className="h-8 rounded-lg w-[124px]" data-testid={`admin-user-role-${u.user_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {roles.map((r) => (
                            <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {saving === u.user_id && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-end">
                    <div className="inline-flex items-center gap-1.5">
                      {u.status === "pending" && (
                        <>
                          <button
                            type="button"
                            onClick={() => approve(u.user_id)}
                            disabled={saving === u.user_id}
                            className="text-[11px] text-emerald-600 hover:underline"
                            data-testid={`admin-user-approve-${u.user_id}`}
                          >{t("usersManager.approve")}</button>
                          <button
                            type="button"
                            onClick={() => reject(u.user_id)}
                            disabled={saving === u.user_id}
                            className="text-[11px] text-rose-600 hover:underline"
                            data-testid={`admin-user-reject-${u.user_id}`}
                          >{t("usersManager.reject")}</button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 rounded-lg text-xs text-muted-foreground hover:text-primary"
                        onClick={() => setActivityUser(u)}
                        title={t("usersManager.openUser", { defaultValue: "Open full profile" })}
                        data-testid={`admin-user-activity-${u.user_id}`}
                      >
                        <History className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 rounded-lg text-xs"
                        onClick={() => setManageUser(u)}
                        data-testid={`admin-user-manage-${u.user_id}`}
                      >
                        <Settings2 className="h-3.5 w-3.5 me-1" />
                        {t("usersManager.manage")}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <AdminUserAccessDialog
        open={!!manageUser}
        onOpenChange={(v) => { if (!v) setManageUser(null); }}
        user={manageUser}
        onSaved={(fresh) => {
          if (!fresh) return;
          setItems((prev) => prev.map((u) => (u.user_id === fresh.user_id ? { ...u, ...fresh } : u)));
        }}
      />

      <AdminUserActivityDialog
        open={!!activityUser}
        onOpenChange={(v) => { if (!v) setActivityUser(null); }}
        user={activityUser}
      />
    </div>
  );
}

export default AdminUsersManager;
